import { create } from 'zustand'
import { callTool } from '../lib/tools'
import { readNodesAgain } from './useNode'
import {
  attachArgs,
  detachResult,
  parseAttachPreview,
  type AttachOptions,
  type AttachPreview,
  type DetachResult,
} from '../utilities/nodeAttach'

/**
 * What a surface asks the dialog to do. ⚠ A surface gives what it knows: the node page gives the
 * node, the network page the network, and the dialog picks the other side.
 */
export type AttachRequest =
  | { mode: 'attach'; nodeId?: string; networkId?: string }
  | { mode: 'detach'; nodeId: string; networkId: string }
  /** After a network delete: the nodes that took a number, and the nodes set to Private. */
  | { mode: 'numbered'; names: string[]; keptPrivate: string[] }

interface NodeAttachState {
  request: AttachRequest | null
  /** ⚠ A write is on the wire. It cannot be called back, so the dialog stays open until it lands. */
  busy: boolean
  /** Every surface calls this. `NodeAttachDialog` is the one dialog, and the router decides. */
  open: (request: AttachRequest) => void
  close: () => void
  /** Writes nothing. THROWS a refusal, as every router call here does. */
  preview: (nodeId: string, networkId: string, nodeName: string) => Promise<AttachPreview>
  attach: (nodeId: string, networkId: string, options: AttachOptions) => Promise<void>
  /** The node's new name, when the detach numbered it, and whether it set the node to Private. */
  detach: (nodeId: string, networkId: string) => Promise<DetachResult>
}

const useNodeAttach = create<NodeAttachState>()(set => ({
  request: null,
  busy: false,
  open: request => set({ request }),
  close: () => set({ request: null }),
  preview: async (nodeId, networkId, nodeName) =>
    parseAttachPreview(await callTool('node_attach', attachArgs(nodeId, networkId, { dryRun: true })), nodeName),
  // ⚠ A node read after each write, not only the realtime event: an event on `nodes` says nothing
  // about a row of `node_networks`.
  attach: async (nodeId, networkId, options) => {
    set({ busy: true })
    try {
      await callTool('node_attach', attachArgs(nodeId, networkId, options))
      await readNodesAgain()
    } finally {
      set({ busy: false })
    }
  },
  detach: async (nodeId, networkId) => {
    set({ busy: true })
    try {
      const payload = await callTool('node_detach', { node: nodeId, network: networkId })
      await readNodesAgain()
      return detachResult(payload)
    } finally {
      set({ busy: false })
    }
  },
}))

export default useNodeAttach
